import type { ProfileCompletionFieldValue, ProfileCompletionPrefill } from 'feature-site-members'

const invalidPrefill = () => new TypeError('profileCompletionPrefill must contain only serializable form values')

const isPlainObject = (value: object) => {
	const prototype = Object.getPrototypeOf(value)
	return prototype === Object.prototype || prototype === null
}

/** Copy the draft before the worker bridge; never send signup credentials through it. */
export function sanitizeProfileCompletionPrefill(value: unknown): ProfileCompletionPrefill | undefined {
	if (value === undefined) {
		return undefined
	}
	if (value === null || typeof value !== 'object' || Array.isArray(value) || !isPlainObject(value)) {
		throw invalidPrefill()
	}

	const ancestors = new Set<object>()
	const copyValue = (fieldValue: unknown, depth: number): ProfileCompletionFieldValue => {
		if (depth > 20) {
			throw invalidPrefill()
		}
		if (
			fieldValue === null ||
			fieldValue === undefined ||
			typeof fieldValue === 'string' ||
			typeof fieldValue === 'boolean'
		) {
			return fieldValue
		}
		if (typeof fieldValue === 'number' && Number.isFinite(fieldValue)) {
			return fieldValue
		}
		if (typeof fieldValue !== 'object' || ancestors.has(fieldValue)) {
			throw invalidPrefill()
		}
		ancestors.add(fieldValue)
		try {
			if (Array.isArray(fieldValue)) {
				return fieldValue.map((item) => copyValue(item, depth + 1))
			}
			if (!isPlainObject(fieldValue)) {
				throw invalidPrefill()
			}
			return Object.fromEntries(Object.entries(fieldValue).map(([key, item]) => [key, copyValue(item, depth + 1)]))
		} finally {
			ancestors.delete(fieldValue)
		}
	}

	// Signup credentials use these reserved form targets; labels/field IDs may differ.
	return Object.fromEntries(
		Object.entries(value)
			.filter(([target]) => target !== 'email' && target !== 'password')
			.map(([target, fieldValue]) => [target, copyValue(fieldValue, 0)])
	)
}
